import React from "react";
import { Button, Icon, ScrollArea } from "@elaniintc/goes-design-system";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@elaniintc/goes-design-system/ui";
import type { FormPage } from "../types";

export interface StepHeaderProps {
  pages: FormPage[];
  selectedPageId: string;
  setSelectedPageId: (id: string) => void;
  onRenameStep: (stepId: string) => void;
  onReorderStep: () => void;
  onDeleteStep: (stepId: string) => void;
  onAddStep: () => void;
}

export const StepHeader: React.FC<StepHeaderProps> = ({
  pages,
  selectedPageId,
  setSelectedPageId,
  onRenameStep,
  onReorderStep,
  onDeleteStep,
  onAddStep,
}) => {
  const currentPageIndex = pages.findIndex((p) => p.id === selectedPageId);
  const totalSteps = pages.length;
  const onlyOneStep = totalSteps === 1;
  const currentPage = pages.find((p) => p.id === selectedPageId);
  return (
    <div className="flex items-center justify-between w-full px-4 py-2 border-b border-neutral-quiet relative">
      {/* Step Name (editable) */}
      <div className="flex items-center gap-4 max-w-[65%]">
        <span className="text-body-loud font-semibold text-ellipsis overflow-hidden whitespace-nowrap">
          {currentPage ? currentPage.name : "Nombre del paso"}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Step Counter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-body-quiet">
            Paso {currentPageIndex + 1} de {totalSteps}
          </span>
        </div>
        {/* Menus */}
        {/* Ellipsis menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="tertiary"
              className="flex w-8 h-8 items-center justify-center rounded-lg border border-neutral"
            >
              <Icon
                type="far"
                name="ellipsis-vertical"
                className="text-body-loud"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[222px] rounded-lg border border-neutral bg-neutral-object shadow-lg p-2"
            side="bottom"
            align="end"
          >
            <ScrollArea className="h-[138px]">
              <DropdownMenuItem
                className="h-11 gap-4 px-3 py-2 text-base font-normal text-body hover:bg-neutral-object-accent-silent cursor-pointer"
                onClick={() => {
                  onRenameStep(selectedPageId);
                }}
              >
                Renombrar paso
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`h-11 px-3 py-2 text-base font-normal text-body ${onlyOneStep ? "text-on-disabled bg-neutral-object-accent-silent" : "text-body hover:bg-neutral-object-accent-silent cursor-pointer"}`}
                disabled={onlyOneStep}
                onClick={!onlyOneStep ? onReorderStep : undefined}
              >
                Cambiar orden
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`h-11 px-3 py-2 text-base font-normal  ${onlyOneStep ? "text-on-disabled bg-neutral-object-accent-silent" : "text-danger hover:bg-neutral-object-accent-silent cursor-pointer"}`}
                disabled={onlyOneStep}
                onClick={
                  !onlyOneStep ? () => onDeleteStep(selectedPageId) : undefined
                }
              >
                Eliminar paso
              </DropdownMenuItem>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Bars menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="tertiary"
              className="flex w-8 h-8 items-center justify-center rounded-lg border border-neutral"
            >
              <Icon type="far" name="bars" className="text-body-loud" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[254px] rounded-lg border border-neutral bg-neutral-object shadow-lg p-2"
            side="bottom"
            align="end"
          >
            <ScrollArea className="h-[254px]">
              {/* Añadir nuevo paso */}
              <DropdownMenuItem
                className="flex h-11 items-center justify-between gap-4 px-3 py-2 text-base font-normal text-body hover:bg-surface-action-accent-quiet cursor-pointer"
                onClick={onAddStep}
              >
                Añadir nuevo paso
                <Icon type="far" name="plus" className="ml-2" />
              </DropdownMenuItem>
              {/* If more than 1 step, show Reordenar pasos */}
              {pages.length > 1 && (
                <DropdownMenuItem
                  className="flex h-11 items-center justify-between gap-4 px-3 py-2 text-base font-normal text-body hover:bg-surface-action-accent-quiet cursor-pointer"
                  onClick={onReorderStep}
                >
                  Reordenar pasos
                  <Icon type="fas" name="arrows-up-down" className="ml-2" />
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="border-neutral-quiet border-t mx-4 my-2.5" />
              {/* Step list */}
              {pages.length === 1 ? (
                <DropdownMenuItem
                  key={pages[0].id}
                  className={`gap-4 px-3 h-11 py-2 flex items-center cursor-pointer bg-surface-action-accent font-semibold rounded-md w-full`}
                  onClick={() => setSelectedPageId(pages[0].id)}
                >
                  <span className="font-semibold text-base text-action">
                    Paso 1
                  </span>
                  <span className="ml-2 text-ellipsis font-normal text-base whitespace-nowrap text-action truncate max-w-[50%]">
                    {pages[0].name}
                  </span>
                </DropdownMenuItem>
              ) : (
                pages.map((page, idx) => {
                  const isSelected = selectedPageId === page.id;
                  return (
                    <DropdownMenuItem
                      key={page.id}
                      className={
                        `px-4 py-2 text-sm h-11 flex items-center gap-2 cursor-pointer hover:bg-surface-action-accent-quiet rounded-md w-full min-w-0 overflow-hidden` +
                        (isSelected
                          ? " bg-surface-action-accent font-semibold"
                          : "")
                      }
                      onClick={() => setSelectedPageId(page.id)}
                    >
                      <div className="flex items-center min-w-0 w-full overflow-hidden">
                        <span
                          className={
                            isSelected
                              ? "font-semibold text-action"
                              : "font-semibold text-body"
                          }
                          style={{ flexShrink: 0 }}
                        >
                          Paso {idx + 1}
                        </span>
                        <span
                          className={
                            isSelected
                              ? "ml-2 font-normal text-action truncate flex-1 min-w-0 max-w-[160px]"
                              : "ml-2 font-normal text-body-quiet truncate flex-1 min-w-0 max-w-[160px]"
                          }
                          title={page.name}
                        >
                          {page.name}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  );
                })
              )}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
